import { useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, Profile } from '@/lib/supabaseClient';
import { lovable } from '@/integrations/lovable';
import { checkSubscription, SubscriptionStatus } from '@/services/stripeService';
import { AuthContext } from './auth.context';
import { API_URL } from '@/lib/apiClient';
import { getOpositorPlan } from '@/lib/authRedirect';

const AUTH_BOOT_TIMEOUT_MS = 7_000;
const AUTH_REQUEST_TIMEOUT_MS = 8_000;

function withTimeout<T>(promise: PromiseLike<T>, timeoutMs: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
    Promise.resolve(promise).then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  const prevUserIdRef = useRef<string | undefined>(undefined);

  const refreshSubscription = useCallback(async () => {
    if (!session) return;

    try {
      const status = await checkSubscription();
      setSubscriptionStatus(status);

      // Update profile plan if it differs from subscription
      // Use functional update to avoid depending on profile in the callback
      if (status.subscribed && status.plan) {
        setProfile(prev => {
          if (prev && prev.plan !== status.plan) {
            return { ...prev, plan: status.plan };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error refreshing subscription:', error);
    }
  }, [session]);

  const syncFreshUser = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        // Solo actualizar si el ID cambió realmente — evita re-renders innecesarios
        if (data.user.id !== prevUserIdRef.current) {
          prevUserIdRef.current = data.user.id;
          setUser(data.user);
        }
      }
    } catch (error) {
      console.warn('Failed to refresh auth user:', error);
    }
  }, []);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const [profileResult, roleResult] = await Promise.all([
        withTimeout(
          supabase.from('profiles').select('*').eq('id', userId).single(),
          AUTH_REQUEST_TIMEOUT_MS,
          'Profile request',
        ),
        withTimeout(
          supabase.from('user_roles').select('role').eq('user_id', userId).single(),
          AUTH_REQUEST_TIMEOUT_MS,
          'Role request',
        ),
      ]);

      if (profileResult.error) throw profileResult.error;
      const role = roleResult.data?.role || 'user';
      setProfile({ ...profileResult.data, opositor_body: (profileResult.data as any).opositor_body ?? null, role } as unknown as Profile);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      // The app must never keep the user at the startup spinner because a profile query is slow.
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const bootWatchdog = window.setTimeout(() => {
      setIsLoading((pending) => {
        if (pending) console.warn('[AUTH] Bootstrap timeout; continuing without a blocking profile request');
        return false;
      });
    }, AUTH_BOOT_TIMEOUT_MS);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);

        // Solo actualizar user si el ID cambió realmente — evita re-renders por token refresh
        const newUserId = currentSession?.user?.id ?? null;
        const prevUserId = prevUserIdRef.current ?? null;
        if (newUserId !== prevUserId) {
          prevUserIdRef.current = newUserId ?? undefined;
          setUser(currentSession?.user ?? null);
        }

        // Fetch profile when session changes
        if (currentSession?.user) {
          setTimeout(() => {
            void fetchProfile(currentSession.user.id);
          }, 0);
        } else {
          setProfile(null);
          setSubscriptionStatus(null);
          setIsLoading(false);
        }
      }
    );

    // Check for existing session — handle stale/expired refresh tokens gracefully
    // Add timeout to prevent infinite loading if server is unreachable
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('getSession timed out')), 5000)
    );

    Promise.race([sessionPromise, timeoutPromise]).then((result: any) => {
      const { data: { session: currentSession }, error } = result;
      // Detect expired / revoked refresh token
      const isTokenError = error && (
        error.message?.includes('refresh_token_not_found') ||
        error.message?.includes('Invalid Refresh Token') ||
        error.code === 'session_not_found'
      );

      if (isTokenError) {
        console.warn('Stale session detected, signing out silently:', error.message);
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        prevUserIdRef.current = undefined;
        setUser(null);
        setProfile(null);
        setSubscriptionStatus(null);
        setIsLoading(false);
        // Avisar al usuario solo si estaba en una zona privada (no en /auth/*)
        if (!window.location.pathname.startsWith('/auth')) {
          import('sonner').then(({ toast }) => {
            toast.info('Tu sesión ha caducado', {
              description: 'Por seguridad, vuelve a iniciar sesión.',
              duration: 6000,
            });
          }).catch(() => {});
        }
        return;
      }

      setSession(currentSession);

      // Solo actualizar user si el ID cambió realmente
      const newUserId = currentSession?.user?.id ?? null;
      const prevUserId = prevUserIdRef.current ?? null;
      if (newUserId !== prevUserId) {
        prevUserIdRef.current = newUserId ?? undefined;
        setUser(currentSession?.user ?? null);
      }

      if (currentSession?.user) {
        void fetchProfile(currentSession.user.id);
      } else {
        setIsLoading(false);
      }
    }).catch((err) => {
      console.warn('Failed to get session on startup:', err);
      // If it looks like a token error, clean up silently
      const msg = err?.message || '';
      if (msg.includes('refresh_token') || msg.includes('Invalid Refresh Token')) {
        supabase.auth.signOut().catch(() => {});
      }
      setIsLoading(false);
    });

    return () => {
      window.clearTimeout(bootWatchdog);
      subscription.unsubscribe();
    };
  }, [fetchProfile, syncFreshUser]);

  // Stable ref for subscription polling interval — prevents re-creation on state changes
  const subIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInitialSubCheck = useRef(false);

  // Auto-refresh subscription on login and periodically
  useEffect(() => {
    if (!session) {
      // Clear interval when logged out
      if (subIntervalRef.current) {
        clearInterval(subIntervalRef.current);
        subIntervalRef.current = null;
      }
      hasInitialSubCheck.current = false;
      return;
    }

    // Skip if we already set up polling for this session
    if (hasInitialSubCheck.current) return;
    hasInitialSubCheck.current = true;

    // Check sessionStorage cache first
    const CACHE_KEY = 'munilex_sub_cache';
    const CACHE_TTL = 10 * 60 * 1000; // 10 minutes
    const cached = sessionStorage.getItem(CACHE_KEY);
    let skipInitialCall = false;

    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          setSubscriptionStatus(data);
          if (data.subscribed && data.plan) {
            setProfile(prev => prev && prev.plan !== data.plan ? { ...prev, plan: data.plan } : prev);
          }
          skipInitialCall = true;
        }
      } catch { /* ignore bad cache */ }
    }

    if (!skipInitialCall) {
      refreshSubscription();
    }

    // Set up polling every 15 minutes using ref (won't reset on re-renders)
    subIntervalRef.current = setInterval(() => {
      // Skip polling for free users — they have no Stripe customer
      setProfile(currentProfile => {
        if (currentProfile?.plan === 'free') {
          console.log('[SUB] Skipping check-subscription for free user');
        } else {
          refreshSubscription();
        }
        return currentProfile; // no mutation
      });
    }, 15 * 60 * 1000);

    return () => {
      if (subIntervalRef.current) {
        clearInterval(subIntervalRef.current);
        subIntervalRef.current = null;
      }
    };
  }, [session]); // Only depend on session — NOT refreshSubscription

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const { error } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        AUTH_REQUEST_TIMEOUT_MS,
        'Sign-in request',
      );
      return { error };
    } catch (error) {
      console.error('Sign-in request failed:', error);
      return { error: { message: 'El acceso está tardando demasiado. Comprueba tu conexión e inténtalo de nuevo.' } };
    }
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    fullName: string,
    userType: 'funcionario' | 'opositor',
    municipalityName?: string,
    autonomousCommunity?: string,
    opositorBody?: string,
    sessionId?: string
  ) => {
    try {
      let plan = 'free';
      if (userType === 'opositor') {
        plan = getOpositorPlan(opositorBody);
      }

      const includeCCAA =
        userType === 'funcionario' ||
        (userType === 'opositor' && opositorBody === 'secretaria');

      const metadata: Record<string, any> = {
        full_name: fullName,
        plan,
        user_type: userType,
        municipality_name: userType === 'funcionario' ? (municipalityName || null) : null,
        autonomous_community: includeCCAA ? (autonomousCommunity || 'extremadura') : null,
        session_id: sessionId || null,
      };
      if (userType === 'opositor' && opositorBody) {
        metadata.opositor_body = opositorBody;
      }

      // Determinar API_URL dinámicamente
      const isProduction = window.location.hostname === 'app.munilex.es' || window.location.hostname.includes('lovable.app');
      const API_URL = import.meta.env.VITE_API_URL || (isProduction ? 'https://app.munilex.es/api' : 'http://localhost:3100/api');
      
      console.log(`[Auth] Using API URL: ${API_URL} for registration`);

      const response = await fetch(`${API_URL}/auth-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          fullName,
          userType,
          municipalityName: metadata.municipality_name,
          autonomousCommunity: metadata.autonomous_community,
          opositorBody: metadata.opositor_body,
          sessionId: metadata.session_id,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Error del servidor (${response.status})`);
      }

      const data = await response.json();

      if (data?.error) {
        const msg = (data.error || '').toLowerCase();
        if (msg.includes('already') || msg.includes('exists') || msg.includes('ya existe')) {
          return { error: { message: 'Ya existe una cuenta con este correo. Inicia sesión.' } };
        }
        if (msg.includes('rate') || msg.includes('demasiados')) {
          return { error: { message: 'Demasiados intentos de registro. Espera unos minutos antes de volver a intentarlo.' } };
        }
        return { error: { message: data.error || 'Error al registrar usuario' } };
      }

      if (data?.error) {
        return { error: { message: data.error } };
      }

      return { error: null };
    } catch (err: any) {
      return { error: { message: err.message || 'Error de conexión' } };
    }
  };

  const signInWithGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth('google', {
      redirect_uri: window.location.origin,
    });
    if (result.error) return { error: result.error };
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setSubscriptionStatus(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isLoading,
        subscriptionStatus,
        refreshSubscription,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
